import type { MessageKey, Params, Translate } from '@/i18n/types';
import { ApiError } from '@/lib/api';

/**
 * An error that names what to say rather than saying it.
 *
 * Thrown from modules that are not components — the encoder, the uploader, the
 * push helpers — and so cannot hold a hook to reach the current locale. The key
 * travels with the error and the screen catching it does the translating, which
 * also means an error raised before a language switch reads correctly after.
 */
export class TranslatableError extends Error {
  readonly params: Params | undefined;

  constructor(
    readonly key: MessageKey,
    params?: Params,
  ) {
    super(key);
    this.name = 'TranslatableError';
    this.params = params;
  }
}

/**
 * The message to show for anything that was thrown.
 *
 * Two kinds of error already say the right thing: a `TranslatableError` names
 * its key, and an `ApiError` carries the server's own message, which the API
 * rendered in the language the request asked for.
 *
 * Everything else is a sentence the browser wrote in its own English — a
 * `TypeError` from `fetch`, a `DOMException` from a codec — and putting that in
 * front of someone reading Russian is not a translation gap that can be closed
 * with another key, because nobody knows what those sentences will say. So the
 * screen shows `fallback` and keeps the original as a parenthetical: it is
 * machine text, deliberately untranslated, and on a phone it is the only bug
 * report anyone is ever going to get. WebKit's "Type error" is one word of use
 * on its own and the whole diagnosis when it follows a sentence.
 */
export function translateError(
  t: Translate,
  error: unknown,
  fallback: MessageKey,
): string {
  if (error instanceof TranslatableError) {
    return t(error.key, error.params);
  }

  if (error instanceof ApiError) {
    return error.message;
  }

  return withDetail(t(fallback), describe(error));
}

/** A translated sentence followed by verbatim technical text, when there is any. */
export function withDetail(message: string, detail: string | null | undefined): string {
  return detail ? `${message} (${detail})` : message;
}

/** What the browser called it, in the form a bug report can use. */
function describe(error: unknown): string | null {
  if (error instanceof Error) {
    // An empty `message` is rare but real; the name alone still identifies it.
    return error.message ? `${error.name}: ${error.message}` : error.name;
  }

  const text = String(error).trim();

  return text === '' ? null : text;
}
